import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertCircle, TrendingUp, HardHat, DollarSign, Clock, Camera, Activity, Sparkles, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const NAVY = "#0F2747";
const CURRENCY = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const ATTN_META = {
    overdue_task: { icon: Clock, tint: "text-destructive" },
    permit_expiring: { icon: AlertCircle, tint: "text-amber-500" },
    insurance_expiring: { icon: AlertCircle, tint: "text-amber-500" },
    reinspection_needed: { icon: AlertCircle, tint: "text-destructive" },
    co_awaiting_approval: { icon: FileText, tint: "text-steel" },
    submittal_stale: { icon: FileText, tint: "text-muted-foreground" },
    draw_due: { icon: DollarSign, tint: "text-emerald-500" },
};

export default function Dashboard() {
    const [data, setData] = useState(null);
    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/dashboard/summary"); setData(data); }
            catch { /* ignore */ }
        })();
    }, []);

    if (!data) return <div className="p-8 text-sm text-muted-foreground">Loading command center…</div>;

    const p = data.portfolio;
    const phaseData = Object.entries(p.phase_counts || {}).map(([k, v]) => ({ phase: k.replace(/_/g, " "), count: v }));

    return (
        <div className="p-6 md:p-10 space-y-8" data-testid="page-dashboard">
            <div>
                <div className="chip chip-navy">Builder Command Center</div>
                <h1 className="mt-3 font-display text-4xl tracking-tight leading-none italic">Good day.</h1>
                <div className="mt-2 text-sm text-muted-foreground">{p.active_projects_count} active projects · {data.attention_total} items need your attention</div>
            </div>

            <section className="space-y-3" data-testid="dashboard-attention">
                <h2 className="overline">Requires your attention</h2>
                {data.attention.length === 0 ? (
                    <div className="border border-border p-6 text-sm text-muted-foreground">Nothing on fire today.</div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-3">
                        {data.attention.map((a, i) => {
                            const M = ATTN_META[a.kind] || { icon: AlertCircle, tint: "text-muted-foreground" };
                            const Ic = M.icon;
                            return (
                                <Link key={i} to={a.project_id ? `/app/projects/${a.project_id}` : "/app/projects"}
                                    className="group border border-border p-4 hover:border-navy transition-colors" data-testid={`attention-${i}`}>
                                    <div className="flex items-start gap-3">
                                        <Ic size={16} className={`shrink-0 mt-0.5 ${M.tint}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium">{a.title}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5">{a.why}</div>
                                            <div className="text-[11px] text-steel mt-2">{a.recommended}</div>
                                        </div>
                                        {a.deadline && <div className="text-[10px] text-muted-foreground shrink-0">{a.deadline.slice(0, 10)}</div>}
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </section>

            <section className="grid md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dashboard-kpis">
                <KPI label="Active projects" value={p.active_projects_count} />
                <KPI label="Total dev budget" value={CURRENCY.format(p.total_dev_budget)} />
                <KPI label="Committed" value={CURRENCY.format(p.total_committed)} />
                <KPI label="Actual spend" value={CURRENCY.format(p.total_actual)} />
                <KPI label="Behind schedule" value={p.behind_schedule_count} tint={p.behind_schedule_count > 0 ? "text-destructive" : ""} />
                <KPI label="Over budget" value={p.over_budget_count} tint={p.over_budget_count > 0 ? "text-destructive" : ""} />
            </section>

            <section className="grid md:grid-cols-2 gap-4">
                <div className="border border-border p-6">
                    <div className="overline mb-3">Projects by phase</div>
                    {phaseData.length ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={phaseData}><XAxis dataKey="phase" fontSize={10} /><YAxis fontSize={10} allowDecimals={false} /><Tooltip /><Bar dataKey="count" fill={NAVY} /></BarChart>
                        </ResponsiveContainer>
                    ) : <div className="text-sm text-muted-foreground">No active projects.</div>}
                </div>
                <div className="border border-border p-6">
                    <div className="overline mb-3">Pipeline vs. expected profit</div>
                    <div className="flex items-center gap-8">
                        <div><div className="text-xs text-muted-foreground">Pipeline value</div><div className="font-display text-3xl italic">{CURRENCY.format(p.pipeline_value)}</div></div>
                        <div><div className="text-xs text-muted-foreground">Expected profit</div><div className="font-display text-3xl italic text-emerald-500">{CURRENCY.format(p.expected_profit)}</div></div>
                    </div>
                </div>
            </section>

            <section className="grid md:grid-cols-3 gap-4">
                <RailBlock title="My open tasks" testid="rail-tasks">
                    {data.my_tasks.length === 0 ? <div className="text-xs text-muted-foreground">No open tasks assigned to you.</div>
                        : data.my_tasks.map((t) => (
                            <Link key={t.id} to={`/app/projects/${t.project_id}`} className="block text-xs py-2 border-b border-border last:border-b-0 hover:bg-secondary/40" data-testid={`my-task-${t.id}`}>
                                <div>{t.name}</div>
                                <div className="text-[10px] text-muted-foreground">Due {t.due_date?.slice(0, 10) || "—"}</div>
                            </Link>
                        ))}
                </RailBlock>
                <RailBlock title="Upcoming inspections" testid="rail-inspections">
                    {data.upcoming_inspections.length === 0 ? <div className="text-xs text-muted-foreground">No inspections in the next 7 days.</div>
                        : data.upcoming_inspections.map((i) => (
                            <div key={i.id} className="text-xs py-2 border-b border-border last:border-b-0" data-testid={`upcoming-insp-${i.id}`}>
                                <div>{i.inspection_type}</div>
                                <div className="text-[10px] text-muted-foreground">{i.scheduled_date?.slice(0, 10)} · {i.inspector_name || "—"}</div>
                            </div>
                        ))}
                </RailBlock>
                <RailBlock title="Recent activity" testid="rail-activity">
                    {data.recent_activity.slice(0, 8).map((a) => (
                        <div key={a.id} className="text-xs py-2 border-b border-border last:border-b-0" data-testid={`recent-activity-${a.id}`}>
                            <div>{a.action}</div>
                            <div className="text-[10px] text-muted-foreground">{(a.timestamp || "").slice(0, 16).replace("T", " ")}</div>
                        </div>
                    ))}
                </RailBlock>
            </section>

            {data.recent_photos?.length > 0 && (
                <section>
                    <h2 className="overline mb-2">Recent progress photos</h2>
                    <div className="grid grid-cols-4 md:grid-cols-8 gap-2" data-testid="dashboard-photos">
                        {data.recent_photos.map((ph) => (
                            <img key={ph.id} src={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${ph.thumbnail_signed}`} alt="" className="w-full h-20 object-cover border border-border" />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

function KPI({ label, value, tint }) {
    return <div className="border border-border p-4"><div className="overline">{label}</div><div className={`mt-1 font-display text-2xl italic ${tint || ""}`}>{value}</div></div>;
}
function RailBlock({ title, children, testid }) {
    return <div className="border border-border p-4" data-testid={testid}><div className="overline mb-2">{title}</div><div>{children}</div></div>;
}
